import {
  loadRemoteEntry,
  loadRemoteModule,
  LoadRemoteModuleOptions
} from '@angular-architects/module-federation-runtime';

const maxAttempts = 3;
const retryDelayMs = 2000;
const failedImports = new Map<string, number>();
const loadedRemotes = new Set<string>();

export async function loadRemoteModuleWithRetry(options: LoadRemoteModuleOptions): Promise<void> {
  if (!('remoteEntry' in options)) {
    await loadRemoteModule(options);
    return;
  }

  const remoteEntry = options.remoteEntry;

  if (loadedRemotes.has(remoteEntry)) {
    await loadRemoteModule({ ...options, remoteEntry: importUrl(remoteEntry) });
    return;
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await ensureReachable(remoteEntry);
      break;
    } catch (error) {
      if (attempt === maxAttempts) {
        throw error;
      }

      await new Promise(resolve => setTimeout(resolve, retryDelayMs * attempt));
    }
  }

  try {
    await loadRemoteEntry({ type: 'module', remoteEntry: importUrl(remoteEntry) });
  } catch (error) {
    failedImports.set(remoteEntry, (failedImports.get(remoteEntry) ?? 0) + 1);
    throw error;
  }

  loadedRemotes.add(remoteEntry);
  await loadRemoteModule({ ...options, remoteEntry: importUrl(remoteEntry) });
}

async function ensureReachable(remoteEntry: string): Promise<void> {
  const response = await fetch(remoteEntry, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`Remote entry '${remoteEntry}' responded ${response.status}`);
  }
}

function importUrl(remoteEntry: string): string {
  const failures = failedImports.get(remoteEntry);

  if (!failures) {
    return remoteEntry;
  }

  const separator = remoteEntry.includes('?') ? '&' : '?';
  return `${remoteEntry}${separator}attempt=${failures}`;
}
